import {
  SubmitProgressDocument,
  type SubmitProgressMutation,
  type SubmitProgressMutationVariables,
  WipForBudgetAndPeriodDocument,
  type WipForBudgetAndPeriodQuery,
  type WipForBudgetAndPeriodQueryVariables,
  WipPeriodsDocument,
  type WipPeriodsQuery,
  type WipPeriodsQueryVariables,
} from "@src/__generated__/graphql";
import { WipPeriodsStore } from "@src/components/ui-kit/MonthPicker/WipPeriodsStore";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { can } from "@src/utils/components/permissions";
import { maxValue, minValue } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { NumberModel } from "@src/utils/models";
import { FieldState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import type { OurWorkProgressBarProps } from "./";

type OurWorkProgressBarStoreSyncArgs = Pick<
  OurWorkProgressBarProps,
  "budgetItemId" | "onSubmitProgress" | "selectedPeriodId"
>;

export class OurWorkProgressBarStore {
  readonly disclosure = new DisclosureState({
    onOpen: () => {
      this.loadPeriods();
    },
  });
  readonly periodsStore = new WipPeriodsStore();
  readonly progressField = new FieldState(new NumberModel(0, 2)).validators(
    minValue(0),
    maxValue(100),
  );

  private readonly periodsFetcher = new FetchHelper<
    WipPeriodsQuery,
    WipPeriodsQueryVariables
  >(WipPeriodsDocument);

  private readonly periodFetcher = new FetchHelper<
    WipForBudgetAndPeriodQuery,
    WipForBudgetAndPeriodQueryVariables
  >(WipForBudgetAndPeriodDocument);

  private readonly submitMutator = new MutationHelper<
    SubmitProgressMutation,
    SubmitProgressMutationVariables
  >(SubmitProgressDocument);

  @observable.ref
  private budgetItemId?: string;

  @observable.ref
  private selectedPeriodId?: string;

  @observable.ref
  private currentPeriodData?: WipForBudgetAndPeriodQuery["wipForBudgetAndPeriod"];

  private onSubmitProgress?: OurWorkProgressBarProps["onSubmitProgress"];

  constructor(args: OurWorkProgressBarStoreSyncArgs = {}) {
    makeObservable(this);
    this.budgetItemId = args.budgetItemId;
    this.selectedPeriodId = args.selectedPeriodId;
    this.onSubmitProgress = args.onSubmitProgress;
  }

  @computed
  get isPeriodsLoading(): boolean {
    return this.periodsFetcher.isLoading.value;
  }

  @computed
  get isPeriodLoading(): boolean {
    return this.periodFetcher.isLoading.value;
  }

  @computed
  get isSubmitLoading(): boolean {
    return this.submitMutator.isLoading.value;
  }

  @computed
  get currentPeriod() {
    return this.periodsStore.currentPeriod;
  }

  @computed
  get periodDisabled(): boolean {
    return !this.currentPeriodData || this.currentPeriodData.is_closed;
  }

  @computed
  get canSubmitProgress(): boolean {
    return can(["wip_submit_all", "wip_submit_team", "wip_submit_own"]);
  }

  @action.bound
  toggle(editable?: boolean): void {
    if (!editable) return;

    if (this.disclosure.isOpen) {
      this.disclosure.onClose();
      return;
    }

    this.disclosure.onOpen();
  }

  @action.bound
  handlePeriodChange(periodId?: string): void {
    this.periodsStore.setPeriodId(periodId);
    this.loadPeriodData();
  }

  async loadPeriods(): Promise<void> {
    if (!this.budgetItemId) {
      return;
    }

    const [data, error] = await this.periodsFetcher.fetch({
      budgetItemId: this.budgetItemId,
    });

    if (error || !data.wipPeriodsMap) {
      return;
    }

    this.periodsStore.setOptions(data.wipPeriodsMap);
    this.periodsStore.setPeriodId(this.selectedPeriodId);

    await this.loadPeriodData();
  }

  async loadPeriodData(): Promise<void> {
    if (!this.budgetItemId || !this.currentPeriod?.id) {
      this.setCurrentPeriodData(undefined);
      return;
    }

    const [data, error] = await this.periodFetcher.fetch({
      our_work_budget_item_id: this.budgetItemId,
      period_id: this.currentPeriod.id,
    });

    if (error) {
      return;
    }

    this.setCurrentPeriodData(data.wipForBudgetAndPeriod);
  }

  async submit(): Promise<void> {
    if (!this.currentPeriodData) {
      console.warn("[OurWorkProgressBarStore.submit]: Empty period data");
      return;
    }

    const { hasError } = await this.progressField.validate();

    if (hasError) {
      return;
    }

    const [data, error] = await this.submitMutator.mutate({
      input: {
        id: this.currentPeriodData.work_in_progress_id,
        progress: this.progressField.value.denominatedValue,
      },
    });

    if (error || !data.submitProgress) {
      return;
    }

    this.onSubmitProgress?.(data.submitProgress);
    this.disclosure.onClose();
  }

  @action.bound
  private setCurrentPeriodData(
    data?: WipForBudgetAndPeriodQuery["wipForBudgetAndPeriod"],
  ): void {
    this.currentPeriodData = data;

    if (!data) {
      this.progressField.reset();
      return;
    }

    this.progressField.onChange(
      this.progressField.value.setDenomValue(data.progress_percent),
    );
  }
}
